import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { PlusIcon, SearchIcon, XIcon } from 'lucide-react';
import { EncabezadoPagina } from '../components/layout/AppLayout';
import { EstadoVacio, Panel } from '../components/ui/Basicos';
import { ChipEstado, ChipMateria } from '../components/ui/Etiquetas';
import { ChipVencimiento } from '../components/ui/Semaforo';
import { useDatos } from '../contexts/DatosContext';
import { abogadoPorId } from '../data/abogados';
import { MATERIAS } from '../data/catalogos';
import { coincideBusqueda, proximoPlazo } from '../utils/consultas';
import type { EstadoExpediente, Materia } from '../types';
export function Expedientes() {
  const {
    expedientes,
    plazos,
    sucursalActiva,
    rol,
    etapasDe,
    sucursales,
    abogados
  } = useDatos();
  const [termino, setTermino] = useState('');
  const [materia, setMateria] = useState<'todas' | Materia>('todas');
  const [estado, setEstado] = useState<'todos' | EstadoExpediente>('todos');
  const [abogadoId, setAbogadoId] = useState('todos');
  const puedeCrear = rol !== 'Abogado';
  const filtrados = useMemo(() => expedientes.filter(e => {
    if (sucursalActiva !== 'todas' && e.sucursalId !== sucursalActiva) return false;
    if (materia !== 'todas' && e.materia !== materia) return false;
    if (estado !== 'todos' && e.estado !== estado) return false;
    if (abogadoId !== 'todos' && !e.abogadoIds.includes(abogadoId)) return false;
    const nombres = e.abogadoIds.map(id => abogadoPorId(id)?.nombre ?? '').join(' ');
    return coincideBusqueda(e, termino, nombres);
  }), [expedientes, sucursalActiva, materia, estado, abogadoId, termino]);
  const claseSelect = 'h-9 rounded border border-gris-200 bg-white px-2.5 text-sm text-gris-700';
  return <>
      <EncabezadoPagina titulo="Expedientes" descripcion="Localizador único por expediente físico, igual que un almacén: sucursal, materia, año y consecutivo." acciones={puedeCrear ? <Link to="/expedientes/nuevo" className="inline-flex h-9 items-center gap-2 rounded bg-marino-800 px-3.5 text-sm font-semibold text-white transition-colors duration-150 hover:bg-marino-700" data-id="element-1443" data-mp="3994qvcbahg8n">
              <PlusIcon className="h-4 w-4" aria-hidden="true" data-id="element-1444" data-mp="2v7scv6fovope" />
              Nuevo expediente
            </Link> : undefined} data-id="element-1442" data-mp="35pe36h2fhwnh" />

      <Panel data-id="element-1445" data-mp="3rwq9ku701wp7">
        <div className="flex flex-wrap items-center gap-2 border-b border-gris-200 px-4 py-3" data-id="element-1446" data-mp="3rx9o00xfy2yh">
          <div className="relative flex h-10 min-w-[22rem] flex-1 items-center" data-id="element-1447" data-mp="3mzqbb5ge5y8n">
            <SearchIcon className="pointer-events-none absolute left-3 h-4 w-4 text-gris-400" aria-hidden="true" data-id="element-1448" data-mp="3i6x66gh0gnw1" />
            <input value={termino} onChange={e => setTermino(e.target.value)} type="search" placeholder="Localizador, cliente, núm. de expediente judicial, materia, abogado o ubicación…" aria-label="Buscar expedientes" className="h-10 w-full rounded border border-gris-200 pl-9 pr-9 text-sm text-gris-800 placeholder:text-gris-400 focus:border-marino-500" data-id="element-1449" data-mp="eiwl5kkltdoj" />
            {termino && <button type="button" onClick={() => setTermino('')} className="absolute right-2.5 text-gris-400 hover:text-gris-700" aria-label="Limpiar búsqueda" data-id="element-1450" data-mp="21i7g1kcjtxm9">
                <XIcon className="h-4 w-4" data-id="element-1451" data-mp="2gc3m1cl8ee6b" />
              </button>}
          </div>

          <select aria-label="Filtrar por materia" value={materia} onChange={e => setMateria(e.target.value as 'todas' | Materia)} className={claseSelect} data-id="element-1452" data-mp="2l3lns2v2wisc">
            <option value="todas" data-id="element-1453" data-mp="2u5uusir51u0z">Todas las materias</option>
            {MATERIAS.map(m => <option key={m} value={m} data-id="element-1454" data-mp="2z4emkzsey019">
                {m}
              </option>)}
          </select>

          <select aria-label="Filtrar por abogado" value={abogadoId} onChange={e => setAbogadoId(e.target.value)} className={claseSelect} data-id="element-1455" data-mp="2lf34zdmmvcsh">
            <option value="todos" data-id="element-1456" data-mp="2v5xpodk8e4bt">Todos los abogados</option>
            {abogados.map(a => <option key={a.id} value={a.id} data-id="element-1457" data-mp="2q8ec7fvcxvkx">
                {a.nombre}
              </option>)}
          </select>

          <select aria-label="Filtrar por estado" value={estado} onChange={e => setEstado(e.target.value as 'todos' | EstadoExpediente)} className={claseSelect} data-id="element-1458" data-mp="1xiys80lpsscv">
            <option value="todos" data-id="element-1459" data-mp="27gvbxgspwvml">Todos los estados</option>
            <option value="Activo" data-id="element-1460" data-mp="27jhibd3vh538">Activo</option>
            <option value="Archivado" data-id="element-1461" data-mp="3nubqt26m9grl">Archivado</option>
            <option value="Concluido" data-id="element-1462" data-mp="3nx7lmhn63pbt">Concluido</option>
          </select>
        </div>

        <div className="flex items-center justify-between px-5 py-2 text-2xs text-gris-500" data-id="element-1463" data-mp="3iyecf1ewykhg">
          <span data-id="element-1464" data-mp="3qhvwcfw2lcej">
            <span className="tabular font-semibold text-gris-700" data-id="element-1465" data-mp="3vew2eqsx6f5n">{filtrados.length}</span> de{' '}
            {expedientes.length} expedientes
          </span>
          <span data-id="element-1466" data-mp="3hgpcbwu7pjgj">Semáforo según días restantes del próximo vencimiento</span>
        </div>

        {filtrados.length === 0 ? <EstadoVacio mensaje="Ningún expediente coincide con la búsqueda" sugerencia="Verifica el localizador o quita algún filtro. La búsqueda también acepta ubicación física (por ejemplo, «Caja 04»)." data-id="element-1467" data-mp="3lija9xd0w9d0" /> : <div className="overflow-x-auto scroll-fino" data-id="element-1468" data-mp="47jhc0ox1hkg">
            <table className="w-full min-w-[1180px] border-t border-gris-200 text-sm" data-id="element-1469" data-mp="8a6g6lk082r5">
              <thead data-id="element-1470" data-mp="87tr7oftws4l">
                <tr className="bg-gris-50 text-left text-2xs uppercase tracking-wide text-gris-500" data-id="element-1471" data-mp="w00p4s17d764">
                  <th scope="col" className="px-5 py-2.5 font-semibold" data-id="element-1472" data-mp="10r0pk79j67dd">
                    Localizador
                  </th>
                  <th scope="col" className="px-3 py-2.5 font-semibold" data-id="element-1473" data-mp="1al69j6ljbfcb">
                    Cliente
                  </th>
                  <th scope="col" className="px-3 py-2.5 font-semibold" data-id="element-1474" data-mp="16lnma468qs6b">
                    Materia
                  </th>
                  <th scope="col" className="px-3 py-2.5 font-semibold" data-id="element-1475" data-mp="wmoq3l2nwp07">
                    Etapa actual
                  </th>
                  <th scope="col" className="px-3 py-2.5 font-semibold" data-id="element-1476" data-mp="rnvb1o7q8pxx">
                    Abogado
                  </th>
                  <th scope="col" className="px-3 py-2.5 font-semibold" data-id="element-1477" data-mp="mw3k3cknsi79">
                    Sucursal
                  </th>
                  <th scope="col" className="px-3 py-2.5 font-semibold" data-id="element-1478" data-mp="it76y1dw1eap">
                    Ubicación física
                  </th>
                  <th scope="col" className="px-3 py-2.5 font-semibold" data-id="element-1479" data-mp="8wud93icf0fy">
                    Próximo vencimiento
                  </th>
                  <th scope="col" className="px-5 py-2.5 font-semibold" data-id="element-1480" data-mp="3zc2r8oogd5u">
                    Estado
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gris-100" data-id="element-1481" data-mp="3uyba4ba1zsa6">
                {filtrados.map(e => {
              const etapas = etapasDe(e.materia);
              const plazo = proximoPlazo(plazos, e.id);
              const sucursal = sucursales.find(s => s.id === e.sucursalId);
              return <tr key={e.id} className="align-top hover:bg-gris-50" data-id="element-1482" data-mp="37a1rfi84zkn9">
                      <td className="px-5 py-3" data-id="element-1483" data-mp="3c7k1xd1sy7xd">
                        <Link to={`/expedientes/${e.id}`} className="font-mono text-xs font-semibold text-marino-700 hover:underline" data-id="element-1484" data-mp="3c8d0hqqlxr4f">
                          {e.localizador}
                        </Link>
                        <p className="tabular mt-0.5 text-2xs text-gris-400" data-id="element-1485" data-mp="2ohpqmzx4srf8">{e.numeroJudicial}</p>
                      </td>
                      <td className="max-w-[15rem] px-3 py-3" data-id="element-1486" data-mp="2tfhhdqm1i7hu">
                        <p className="truncate font-medium text-gris-800" data-id="element-1487" data-mp="bjpnmzkcywuf">{e.cliente}</p>
                        <p className="truncate text-2xs text-gris-400" data-id="element-1488" data-mp="7huhnhrhfvab">{e.tipoAsunto}</p>
                      </td>
                      <td className="px-3 py-3" data-id="element-1489" data-mp="lhn616fjdzf4">
                        <ChipMateria materia={e.materia} data-id="element-1490" data-mp="ghr4rey84m39" />
                      </td>
                      <td className="max-w-[11rem] px-3 py-3" data-id="element-1491" data-mp="gk3u4d6b9yqc">
                        <p className="text-xs text-gris-700" data-id="element-1492" data-mp="ug7ihpui7cuw">
                          {etapas[e.etapaActual]?.nombre ?? 'Flujo sin configurar'}
                        </p>
                        <p className="tabular text-2xs text-gris-400" data-id="element-1493" data-mp="147levor5rwjt">
                          {etapas.length > 0 ? `Etapa ${e.etapaActual + 1} de ${etapas.length}` : 'Sin etapas definidas'}
                        </p>
                      </td>
                      <td className="px-3 py-3" data-id="element-1494" data-mp="1977sw0et9gto">
                        <p className="text-xs text-gris-700" data-id="element-1495" data-mp="194bvqeasew6a">
                          {abogadoPorId(e.abogadoIds[0])?.nombre}
                        </p>
                        {e.abogadoIds.length > 1 && <p className="text-2xs text-gris-400" data-id="element-1496" data-mp="1wrh1tyh2wwlr">
                            +{e.abogadoIds.length - 1} más asignado
                          </p>}
                      </td>
                      <td className="px-3 py-3 text-xs text-gris-700" data-id="element-1497" data-mp="194hbrf38x4y3">{sucursal?.nombre}</td>
                      <td className="px-3 py-3" data-id="element-1498" data-mp="1duxthbe3ncq9">
                        <p className="text-xs text-gris-700" data-id="element-1499" data-mp="1dvqya6thzs5r">
                          {e.ubicacion.almacen} · {e.ubicacion.pasillo}
                        </p>
                        <p className="text-2xs text-gris-400" data-id="element-1500" data-mp="15247kv45df3f">
                          {e.ubicacion.estante} · {e.ubicacion.caja}
                        </p>
                      </td>
                      <td className="px-3 py-3" data-id="element-1501" data-mp="v4g9bv7c1pgi">
                        <ChipVencimiento fechaLimite={plazo?.fechaLimite} data-id="element-1502" data-mp="v3y7mj1wij17" />
                      </td>
                      <td className="px-5 py-3" data-id="element-1503" data-mp="x0ujdkeyw409">
                        <ChipEstado estado={e.estado} data-id="element-1504" data-mp="1218tr941jq1g" />
                        {e.prestadoA && <p className="mt-1 text-2xs font-medium text-ambar-700" data-id="element-1505" data-mp="n4hr8qz2ly7m">Prestado</p>}
                      </td>
                    </tr>;
            })}
              </tbody>
            </table>
          </div>}
      </Panel>
    </>;
}